//Charger Scan Handler manages incoming QR scans
// The Serial Number will be turned into the charger UUID
// The charger will be selected
// And the ChargingStation screen will be loaded

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
  useIsFocused,
} from '@react-navigation/native';
import {
  chargerQrScan,
  getMyDistanceFromCharger,
  getMyDrivingTime,
} from '../../store/actions/charging-actions';
import { RootState } from '../../store';
import { ChargerAttributes } from '../../models/charger-model';
import * as Location from 'expo-location';
import { Platform } from 'react-native';
import { notificationService } from '../../services/notification-service';
import type { RootStackParamList } from '../../navigation/stackTypes';
import {
  getUserSession,
  getUserSessionObj,
} from '../../services/user-session-service';
import { UserSessionAttributes } from '../../models/user-session-model';
import { setSession } from '../../store/actions/user-session';
import { AppDispatch } from '../../store';
import { getUserLocation } from './utils/get-user-location';
import AsyncStorage from '@react-native-async-storage/async-storage';

const ChargerScanHandler = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  let [myLocation, setMyLocation] =
    useState<Location.LocationObjectCoords | null>(null);
  const isFocused = useIsFocused();

  const route = useRoute<RouteProp<RootStackParamList, 'ChargerScan'>>();
  const { chargerSerial } = route.params;

  const selectedCharger: ChargerAttributes = useSelector(
    (state: RootState) => state.chargers.selectedCharger
  );
  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const isModeChanging = useSelector(
    (state: RootState) => state.user.isModeChanging
  );

  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    const fetchChargerAndNavigate = async () => {
      if (session) {
        //no need to try to fetch if we are not logged in and there is no user session
        try {
          dispatch(chargerQrScan(chargerSerial));
        } catch (error) {
          console.error('Error:', error);
        }
      }
    };

    fetchChargerAndNavigate();
  }, [chargerSerial, dispatch, navigation, session]);

  useEffect(() => {
    // Don't run this effect at all if we're in a mode change
    if (isModeChanging) {
      console.log(
        'ChargerScanHandler: Skipping session setup (mode change in progress)'
      );
      return;
    }

    //log in the user
    const getSessionObj = async () => {
      const sessionObj = await getUserSessionObj();
      dispatch(setSession(sessionObj));
    };

    const requestLocation = async () => {
      const userLocation = await getUserLocation();
      setMyLocation(userLocation);
    };

    getSessionObj();
    requestLocation();
  }, [isModeChanging]);

  useEffect(() => {
    const loadMyInfo = async () => {
      // Removed myLocation requirement - page should load even without location services
      if (!isFocused || !session) return;
      
      if (selectedCharger.uuid === 'failed') {
        navigation.navigate('ChargingStationNotFound', {
          chargerSerial: chargerSerial,
        });
        return;
      }
      
      // Navigate even without location - just show fallback values if location unavailable
      if (selectedCharger.uuid && selectedCharger.uuid !== '' && session) {
        try {
          let distanceStr = 'N/A';
          let drivingTimeStr = 'N/A';
          
          // Only calculate distance/time if location services are available
          if (myLocation) {
            const myDistance: number = getMyDistanceFromCharger(
              myLocation,
              selectedCharger
            );
            const myDrivingTime: string = await getMyDrivingTime(
              myLocation,
              selectedCharger.uuid
            );
            
            distanceStr = myDistance?.toString() || 'N/A';
            drivingTimeStr = myDrivingTime || 'N/A';
          }
          
          navigation.navigate('ChargerStation', {
            myDistanceFromCharger: distanceStr,
            myTimeFromCharger: drivingTimeStr,
          }); // Navigate to the ChargerStation screen
        } catch (error) {
          console.error('Error loading charger info:', error);
          // Still navigate but with fallback values
          navigation.navigate('ChargerStation', {
            myDistanceFromCharger: 'N/A',
            myTimeFromCharger: 'N/A',
          });
        }
      }
    };

    loadMyInfo();
    console.log('Trying to navigate to charger:', selectedCharger);
  }, [selectedCharger, myLocation, session, isFocused]);

  return null; // No UI is rendered by this component
};

export default ChargerScanHandler;
